import { Link } from "react-router-dom";
import { useDashboard, type Dashboard } from "../../api/dashboard";
import { formatDate } from "../../api/dates";
import { useMe } from "../../auth/session";
import { PageHead } from "../../components/ui";
import "../../styles/dashboard.css";
import { CommercialCard } from "./CommercialCard";
import { GroupCard } from "./GroupCard";
import { MapCard } from "./MapCard";
import { OfficeQueue } from "./OfficeQueue";
import { StatusCard } from "./StatusCard";

/** Where everything stands, and where to go next: every number links to the
 * screen that drills into it, so the dashboard is a router, not a report.
 * What is waiting on the office comes first, because that is what somebody
 * opening it at eight in the morning is looking for. */
export default function DashboardPage() {
  const view = useDashboard();
  // Workflow is the office's screen; a crew account would only be refused it.
  const office = useMe().data?.role === "office";
  if (view.isPending) {
    return <p className="app-loading">Loading the dashboard…</p>;
  }
  if (view.isError) {
    return <p className="notice bad">The dashboard could not be loaded: {view.error.message}</p>;
  }
  return <DashboardView data={view.data} showWorkflow={office} />;
}

export function DashboardView({ data, showWorkflow = false }: { data: Dashboard; showWorkflow?: boolean }) {
  const colours = Object.fromEntries(data.status_key.map((k) => [k.status, k.color]));
  const contract = data.contract_codes.length > 0 ? ` · contract ${data.contract_codes.join(", ")}` : "";
  return (
    <div className="dashboard-page">
      <PageHead
        title="Where things stand"
        sub={`${formatDate(data.today)}${contract}`}
        actions={
          showWorkflow ? (
            <Link className="go-link" to="/workflow">
              Open Workflow →
            </Link>
          ) : undefined
        }
      />
      <OfficeQueue tiles={data.office_queue} />
      <StatusCard data={data} colours={colours} />
      <div className="dash-pair">
        <GroupCard data={data} colours={colours} />
        <MapCard data={data} colours={colours} />
      </div>
      {data.commercial === null ? null : <CommercialCard steps={data.commercial.steps} />}
    </div>
  );
}
